const BLOCKED_LAYOUT_STYLE_PROPS = new Set([
  'width',
  'min-width',
  'max-width',
  'height',
  'min-height',
  'max-height',
  'position',
  'left',
  'right',
  'top',
  'bottom',
  'float',
  'clear',
  'display',
  'white-space',
  'overflow',
  'overflow-x',
  'overflow-y',
  'transform',
  'text-indent',
  'margin-left',
  'margin-right',
  'padding-left',
  'padding-right'
]);

const WIDTH_RELATED_ATTRS = [
  'width',
  'height',
  'cellpadding',
  'cellspacing',
  'align',
  'valign'
];

const NBSP_CHARS = /\u00A0/g;
const ZERO_WIDTH_CHARS = /[\u200B-\u200D\uFEFF]/g;
const SPACES_BEFORE_PUNCTUATION = /[ \t\f\v]+([,.;:!?])/g;

export interface NormalizeRichHtmlForDisplayOptions {
  preserveClasses?: boolean;
  preserveIds?: boolean;
}

function cleanInlineStyle(styleValue: string): string {
  const declarations = styleValue
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);

  const keptDeclarations: string[] = [];

  for (const declaration of declarations) {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex <= 0) {
      continue;
    }

    const prop = declaration.slice(0, separatorIndex).trim().toLowerCase();
    if (!prop) {
      continue;
    }

    if (prop.startsWith('mso-')) {
      continue;
    }

    if (BLOCKED_LAYOUT_STYLE_PROPS.has(prop)) {
      continue;
    }

    keptDeclarations.push(declaration);
  }

  return keptDeclarations.join('; ');
}

function unwrapNode(element: Element): void {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function normalizeTextNodes(container: HTMLElement): void {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  for (const textNode of textNodes) {
    const rawValue = textNode.nodeValue;
    if (!rawValue) {
      continue;
    }

    const parentTagName = textNode.parentElement?.tagName.toLowerCase();
    if (parentTagName === 'pre' || parentTagName === 'code') {
      continue;
    }

    const normalizedValue = rawValue
      .replace(NBSP_CHARS, ' ')
      .replace(ZERO_WIDTH_CHARS, '')
      .replace(SPACES_BEFORE_PUNCTUATION, '$1');

    if (normalizedValue !== rawValue) {
      textNode.nodeValue = normalizedValue;
    }
  }
}

export function normalizeRichHtmlForDisplay(
  html: string,
  options: NormalizeRichHtmlForDisplayOptions = {}
): string {
  if (!html) {
    return '';
  }

  if (typeof document === 'undefined') {
    return html;
  }

  const preserveClasses = options.preserveClasses ?? true;
  const preserveIds = options.preserveIds ?? true;

  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('script, style, link, meta, iframe, object, embed').forEach((el) => el.remove());

  const elements = Array.from(container.querySelectorAll('*'));
  for (const element of elements) {
    const tagName = element.tagName.toLowerCase();

    if (tagName.includes(':')) {
      unwrapNode(element);
      continue;
    }

    for (const attr of WIDTH_RELATED_ATTRS) {
      element.removeAttribute(attr);
    }

    if (!preserveIds) {
      element.removeAttribute('id');
    }

    const className = element.getAttribute('class');
    if (className) {
      if (!preserveClasses || /(mso|wordsection)/i.test(className)) {
        element.removeAttribute('class');
      }
    }

    const styleValue = element.getAttribute('style');
    if (styleValue) {
      const cleanedStyle = cleanInlineStyle(styleValue);
      if (cleanedStyle) {
        element.setAttribute('style', cleanedStyle);
      } else {
        element.removeAttribute('style');
      }
    }

    if (tagName === 'table') {
      const table = element as HTMLElement;
      table.classList.add('rich-html-table-no-scroll');
      table.style.setProperty('width', '100%');
      table.style.setProperty('max-width', '100%');
      table.style.setProperty('table-layout', 'fixed');
      table.style.setProperty('border-collapse', 'collapse');
    }

    if (tagName === 'td' || tagName === 'th') {
      element.removeAttribute('nowrap');
      const cell = element as HTMLElement;
      cell.style.setProperty('white-space', 'normal');
      cell.style.setProperty('overflow-wrap', 'anywhere');
      cell.style.setProperty('word-break', 'break-word');
    }

    if (tagName === 'span' && element.attributes.length === 0) {
      unwrapNode(element);
    }
  }

  normalizeTextNodes(container);

  return container.innerHTML;
}
